// src/features/export/exportFormats/svgExport.ts
/**
 * SVG Export Handler - FIXED to use html-to-image
 * Captures the DOM and exports as SVG using html-to-image library
 */

import { toSvg } from 'html-to-image';
import { Model } from '../../../types';
import {
    ExportHandler,
    ExportResult,
    ExportConfig,
    ExportProgressCallback,
} from '../exportTypes';
import {
    generateFilename,
    createExportMetadata,
} from '../exportUtils';
import { beginVisualExport } from '../exportPreparation';

export class SVGExportHandler implements ExportHandler {
    public readonly format = 'svg' as const;
    private currentConfig: ExportConfig | null = null;

    /**
     * Detect current theme (dark/light mode)
     */
    private detectCurrentTheme(): 'dark' | 'light' {
        if (document.documentElement.classList.contains('dark')) return 'dark';
        if (document.body.classList.contains('dark')) return 'dark';
        const muiTheme = document.querySelector('[data-mui-color-scheme]');
        if (muiTheme?.getAttribute('data-mui-color-scheme') === 'dark') return 'dark';
        if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
        return 'light';
    }

    /**
     * Get theme-appropriate background color
     */
    private getThemeBackgroundColor(userColor?: string, transparent?: boolean): string | undefined {
        if (transparent) return undefined;
        if (userColor) return userColor;

        // Auto-detect theme and return appropriate color
        const theme = this.detectCurrentTheme();
        return theme === 'dark' ? '#1e293b' : '#ffffff';
    }

    async export(
        element: HTMLElement,
        model: Model,
        config: ExportConfig,
        onProgress?: ExportProgressCallback
    ): Promise<ExportResult> {
        try {
            // Store config for use in shouldIncludeNode method
            this.currentConfig = config;

            onProgress?.(0, 'Starting SVG export...');

            onProgress?.(20, 'Preparing canvas...');

            const backgroundColor = this.getThemeBackgroundColor(
                config.backgroundColor,
                config.transparent
            );
            const restoreExportState = beginVisualExport(element, backgroundColor);

            // 🔥 CRITICAL: Inject CSS to hide UI elements while preserving label borders
            const removeHideStyles = this.injectHideStyles();
            const cleanup = () => {
                removeHideStyles();
                restoreExportState();
            };

            // Wait for React to re-render and styles to apply
            await new Promise(resolve => setTimeout(resolve, 500));

            onProgress?.(40, 'Capturing diagram...');

            // Determine background color
            // Suppress console warnings for known harmless issues
            const originalError = console.error;
            const suppressedPatterns = [
                'chrome-extension://',
                'fabric-icons',
                'leelawadeeui',
                'font',
                'Status:0',
                'Status:404',
                'ERR_FILE_NOT_FOUND'
            ];

            console.error = (...args: any[]) => {
                const msg = args.join(' ').toString();
                const shouldSuppress = suppressedPatterns.some(pattern =>
                    msg.toLowerCase().includes(pattern.toLowerCase())
                );
                if (!shouldSuppress) {
                    originalError(...args);
                }
            };

            try {
                onProgress?.(60, 'Rendering SVG...');

                // Export with html-to-image toSvg
                const svgDataUrl = await toSvg(element, {
                    cacheBust: true,
                    backgroundColor: backgroundColor,
                    // ✨ Filter function to exclude UI elements
                    filter: (node: HTMLElement) => this.shouldIncludeNode(node),
                    // Style overrides
                    style: {
                        transform: 'none'
                    }
                });

                console.error = originalError;
                cleanup();

                onProgress?.(90, 'Finalizing SVG...');

                // Convert data URL to string (SVG is text-based)
                const svgString = this.dataUrlToSvgString(svgDataUrl);

                // Generate filename
                const filename = config.filename || generateFilename(
                    model.title || 'flowi-diagram',
                    'svg'
                );

                // Create metadata
                const metadata = createExportMetadata(
                    config,
                    element.offsetWidth,
                    element.offsetHeight,
                    model.title
                );

                onProgress?.(100, 'SVG export complete!');

                console.log('✅ SVG export successful!');

                return {
                    success: true,
                    data: svgString,
                    filename,
                    format: 'svg',
                    metadata,
                };

            } finally {
                console.error = originalError;
                cleanup();
            }
        } catch (error) {
            console.error('❌ SVG export failed:', error);
            return {
                success: false,
                filename: '',
                error: error as Error,
            };
        }
    }

    /**
     * Convert data URL to SVG string
     */
    private dataUrlToSvgString(dataUrl: string): string {
        // Remove the data URL prefix (data:image/svg+xml;charset=utf-8,)
        const base64Prefix = 'data:image/svg+xml;base64,';
        const utf8Prefix = 'data:image/svg+xml;charset=utf-8,';

        if (dataUrl.startsWith(base64Prefix)) {
            // Decode base64
            const base64Data = dataUrl.substring(base64Prefix.length);
            return atob(base64Data);
        } else if (dataUrl.startsWith(utf8Prefix)) {
            // Decode URI component
            const encodedData = dataUrl.substring(utf8Prefix.length);
            return decodeURIComponent(encodedData);
        }

        // Fallback: try to decode as URI
        return decodeURIComponent(dataUrl.split(',')[1] || dataUrl);
    }

    /**
     * Inject CSS to hide UI elements during export while preserving label borders
     */
    private injectHideStyles(): (() => void) {
        const styleEl = document.createElement('style');
        styleEl.id = 'flowi-svg-export-hide-styles';
        styleEl.textContent = `
            /* ============ HIDE UI ELEMENTS ============ */
            [data-element="transform-control"],
            [data-element="cursor"],
            [data-interaction-layer="true"],
            .MuiBackdrop-root,
            .MuiDialog-root,
            .transform-control,
            .selection-box,
            .resize-handle,
            .anchor-circle,
            .control-point,
            .connector-handle {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
            }

            /* ============ REMOVE SELECTION/INTERACTION BORDERS ============ */
            /* Remove borders from ALL elements except labels */
            * :not([data-flowi-label]) {
                border: none !important;
                outline: none !important;
                box-shadow: none !important;
            }

            /* ============ PRESERVE LABEL BORDERS ============ */
            [data-flowi-label] {
                /* Labels keep their borders - don't override */
            }

            /* ============ REMOVE INTERACTION HIGHLIGHTS ============ */
            .selected,
            .highlighted,
            .hover,
            [data-selected="true"],
            [data-highlighted="true"] {
                outline: none !important;
                border: none !important;
                box-shadow: none !important;
            }
        `;

        document.head.appendChild(styleEl);

        // Return cleanup function
        return () => {
            const el = document.getElementById('flowi-svg-export-hide-styles');
            if (el) {
                el.remove();
            }
        };
    }

    /**
     * Filter function to determine if a node should be included in export
     */
    private shouldIncludeNode(node: HTMLElement): boolean {
        if (!(node instanceof HTMLElement)) {
            return true;
        }

        // Exclude grid if includeGrid is false
        if (this.currentConfig && !this.currentConfig.includeGrid && node.getAttribute('data-grid') === 'true') {
            return false;
        }

        // Exclude elements with these classes
        const excludeClasses = [
            'MuiBackdrop',
            'MuiDialog',
            'transform-control',
            'selection-box',
            'resize-handle',
            'anchor-circle',
            'control-point',
            'connector-handle'
        ];

        for (const cls of excludeClasses) {
            if (node.classList.contains(cls)) {
                return false;
            }
        }

        // Exclude by data attributes
        if (node.getAttribute('data-element') === 'transform-control') return false;
        if (node.getAttribute('data-element') === 'cursor') return false;
        if (node.getAttribute('data-interaction-layer') === 'true') return false;

        // Exclude hidden elements
        if (node.style.display === 'none') return false;
        if (node.style.visibility === 'hidden') return false;
        if (node.style.opacity === '0') return false;

        return true;
    }
}

/**
 * Create SVG export handler instance
 */
export const createSVGExportHandler = (): SVGExportHandler => {
    return new SVGExportHandler();
};
